import { CustomerSpireModel } from "@dillon/Services/PersonalizedDashboardAccount";
import { getCustomerSpire } from "@dillon/Services/PersonalizedDashboardAccount";
import { CustomState } from "@dillon/Store/Reducers";
import { mediaQueriesBP } from "@dillon/Utilities/variables";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState & CustomState) => {
    return {
        session: getSession(state),
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface PersonalizedDashboardAccountStyles {
    wrapper?: InjectableCss;
    logoWrapper?: InjectableCss;
    textWrapper?: InjectableCss;
    firstNameText?: TypographyProps;
    accountNumberText?: TypographyProps;
    accountNumber?: TypographyProps;
    logo?: LazyImageProps;    
    centeringWrapper?: InjectableCss;
}

export const personalizedDashboardAccountStyles: PersonalizedDashboardAccountStyles = {
    wrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: 20px;
            margin-block: 20px;

            ${mediaQueriesBP.minMD} {
                margin-block: 40px;
            }
        `
    },
    logoWrapper: {
        css: css`
            aspect-ratio: 1/1;
            width: 70px;
            height: 70px;
        `
    },
    logo: {
        css: css`
            width: 100%;
            height: 100%;
            object-fit: contain;
        `
    },
    firstNameText: {
        variant: "p",
        size: 22,
        weight: "bold",
        css: css`
            margin-block: 0 5px;
        `
    },
    accountNumberText: {
        variant: "p",
        size: 14,
        weight: "bold",
        css: css`
            display: inline;
        `
    },
    accountNumber: {
        variant: "p",
        size: 14,
        css: css`
            display: inline;
        `
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    }
};

const styles = personalizedDashboardAccountStyles;

const PersonalizedDashboardAccount: React.FC<Props> = ({ session }: Props) => {
    const [customerSpire, setCustomerSpire] = useState<CustomerSpireModel>();
    const [firstName, setFirstName] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(true);
    
    useEffect(() => {
        getCustomerSpire({customerId: session.billToId ?? ""})
        .then((data) => {
            if (data) {
                setCustomerSpire(data);
                setLoading(false);
            }
        })
        
        setFirstName(session.firstName);
    }, []);

    if (loading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }
    
    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.logoWrapper}>
                <LazyImage
                    src={customerSpire?.logoImagePath}
                    {...styles.logo}
                />
            </StyledWrapper>
            <StyledWrapper {...styles.textWrapper}>
                <Typography {...styles.firstNameText}>{`${translate("ISC_Welcome")}, ${firstName}`}</Typography>
                <StyledWrapper>
                    <Typography as="span" {...styles.accountNumberText}>{`${translate("ISC_Account_#")}: `} </Typography>
                    <Typography as="span" {...styles.accountNumber}>{customerSpire?.customerNumber}</Typography>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(PersonalizedDashboardAccount),
    definition: {
        displayName: "Personalized Dashboard Account",
        group: "Verndale Box" as any
    },
};

export default widgetModule;
